import { SHIP_LADDER, SHIP_LADDERS, WORLD_LADDERS, ladderPoint, ladderPosition, localToWorld, type LadderSpec, type PlayerState, type Pose, type Vec3, type WorldLadder } from "@tiny-seas/shared";

/** The ladder a climbing player is on: a ship's hull ladder or a tower ladder. */
export interface ClimbTarget {
  /** Stable identity: ship slot, or 100 + WORLD_LADDERS index. */
  key: number;
  spec: LadderSpec;
  /** Interpolated ship render pose for hull ladders. */
  ship: Pose | null;
  tower: WorldLadder | null;
  generation: number;
}

/** Resolves the ladder a player is climbing into `out`; `ship` is the slot's interpolated render pose. */
export function climbTarget(player: PlayerState | undefined, ship: Pose | null, generation: number, out: ClimbTarget): ClimbTarget | null {
  if (player?.station !== "ladder") return null;
  const tower = WORLD_LADDERS[player.climbLadder];
  if (tower) {
    out.key = 100 + player.climbLadder;
    out.spec = tower.spec;
    out.ship = null;
    out.tower = tower;
    out.generation = 0;
    return out;
  }
  if (!ship) return null;
  out.key = player.climbShip;
  out.spec = SHIP_LADDERS[player.shipLadder] ?? SHIP_LADDER;
  out.ship = ship;
  out.tower = null;
  out.generation = generation;
  return out;
}

export function newClimbTarget(): ClimbTarget {
  return { key: -1, spec: SHIP_LADDER, ship: null, tower: null, generation: -1 };
}

/** Smooth the replicated path, not the ship: contacts stay on the rendered hull. */
export class ClimbPresentation {
  readonly position: Vec3 = { x: 0, y: 0, z: 0 };
  progress = 0;
  private readonly offset: Vec3 = { x: 0, y: 0, z: 0 };
  private slot = -1;
  private generation = -1;
  private blend = 0;
  private initialized = false;

  invalidate(shipSlot: number): boolean {
    if (this.slot !== shipSlot) return false;
    this.initialized = false;
    this.blend = 0;
    this.slot = -1;
    this.generation = -1;
    return true;
  }

  update(dt: number, player: PlayerState | undefined, ladder: ClimbTarget | null, patchAge: number, base: Vec3): Vec3 {
    const climbing = !!ladder && !!player && !player.dead && player.connected;
    const nextSlot = climbing ? ladder.key : -1;
    const generation = ladder?.generation ?? -1;
    const reset = !this.initialized || !!player?.dead || !player?.connected || (climbing && this.slot === nextSlot && this.generation !== generation);
    const changed = nextSlot !== this.slot;
    const target = base;
    if (climbing) {
      const progress = Math.max(0, Math.min(1, player.climbProgress + player.climbRate * patchAge / ladder.spec.seconds));
      this.progress = changed || reset ? player.climbProgress : this.progress + (progress - this.progress) * (1 - Math.exp(-dt / 0.08));
      ladderPosition(this.progress, target, ladder.spec);
      if (ladder.tower) ladderPoint(ladder.tower, target, target);
      else localToWorld(ladder.ship!, target.x, target.y, target.z, target);
    }
    if (reset) {
      this.blend = 0;
    } else if (changed) {
      this.offset.x = this.position.x - target.x;
      this.offset.y = this.position.y - target.y;
      this.offset.z = this.position.z - target.z;
      this.blend = 0.12;
    }
    const weight = this.blend / 0.12;
    this.position.x = target.x + this.offset.x * weight;
    this.position.y = target.y + this.offset.y * weight;
    this.position.z = target.z + this.offset.z * weight;
    this.blend = Math.max(0, this.blend - dt);
    this.slot = nextSlot;
    this.generation = generation;
    this.initialized = true;
    return this.position;
  }
}
